import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, NonNullableFormBuilder } from '@angular/forms';
import { residenceFormRules } from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import { ResidenceInfo } from 'graphql-client';
import { isEqual } from 'lodash';
import { map, Observable, startWith, takeUntil, tap, zip } from 'rxjs';

import { error_sign } from '../../assets/constants';
import { AWSService } from '../services/aws.service';
import { PrequalService } from '../services/prequal.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS } from '../shared/constants/fsevents';
import { CrossComponentDateValidationService } from '../validators/cross-component-date-validation.service';
import { SiblingValidationService } from '../validators/sibling-validation.service';
import { residenceFieldValidator } from './validation/residence-field.validator';
import { residenceMonthResidingValidator } from './validation/residence-month-residing.validator';
import { ResidenceZipCodeAsyncValidator } from './validation/residence-zip-code.async-validator';

type TSelectOptions = { label: string; value: string };
declare const FS: typeof FullStory;

@Component({
  selector: 'capq-residence',
  templateUrl: './residence.component.html',
  styleUrls: ['./residence.component.scss'],
})
export class ResidenceComponent extends Unsubscribe implements OnInit {
  @Output() activeStep: EventEmitter<number> = new EventEmitter<number>();
  @Input() adobeScriptLoaded = false;
  pageViewFired = false;
  initState = {
    addressLine1: ['', residenceFieldValidator()],
    addressLine2: new FormControl<string | undefined | null>(
      '',
      residenceFieldValidator()
    ),
    city: ['', residenceFieldValidator()],
    userState: ['', residenceFieldValidator()],
    monthResiding: new FormControl<string | undefined | null>('', [
      residenceFieldValidator(),
      residenceMonthResidingValidator(),
    ]),
    zipCode: [
      '',
      [residenceFieldValidator()],
      this.residenceZipCodeAsyncValidator.validate.bind(
        this.residenceZipCodeAsyncValidator
      ),
    ],
    ownedBy: new FormControl<string | undefined | null>(
      '',
      residenceFieldValidator()
    ),
  };
  residenceForm = this.formBuilder.group(this.initState);

  profinityFlag = false;
  dobMisMatchError = false;
  isWaiting = false;
  futureDateError = false;
  zipCodeErrorFlag = false;
  usStates: TSelectOptions[] = [];
  ownedByList: TSelectOptions[] = [];
  options = {
    types: [],
    componentRestrictions: { country: 'UA' },
  };
  firstName = '';
  personalData: any; // FIXME: this probably can be typed up
  exclamation = error_sign;
  formFieldData: any; // FIXME: this probably can be typed up
  globalText: any; // FIXME: this probably can be typed up
  formIsIncomplete$!: Observable<boolean>;
  isGoogleAutoCompleteWorking: boolean = false;
  formStartEventEmitted = false;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly awsService: AWSService,
    private prequalService: PrequalService,
    private readonly residenceZipCodeAsyncValidator: ResidenceZipCodeAsyncValidator,
    private siblingValidationService: SiblingValidationService,
    private cdRef: ChangeDetectorRef,
    private crossComponentDateValidation: CrossComponentDateValidationService
  ) {
    super();

    this.crossComponentDateValidation.registerResidencyValidator(() => {
      this.residenceForm.controls.monthResiding.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.residenceForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.prequalService.setResidenceData({
          city: this.residenceForm.controls.city.value,
          userState: this.residenceForm.controls.userState.value,
        } as ResidenceInfo);
        if (this.adobeScriptLoaded && !this.pageViewFired) {
          adobeAnalytics.emitEvent('cac-page-view', 'residence', {
            form_design: 'v 1.0',
            section_name: 'residence',
            form_submission_id: '',
            dealer_list: '',
            form_submission_status: '',
            event_type: 'cac-page-view',
          });
          this.pageViewFired = true;
        }
        if (
          this.adobeScriptLoaded &&
          !isEqual(this.initState, this.residenceForm.getRawValue()) &&
          !this.formStartEventEmitted
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('residence');
        }
      });

    this.residenceForm.controls.ownedBy.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (!(typeof FS === 'undefined')) {
          FS.event(FS_EVENTS.RESIDENCE_SECTION_COMPLETE, {
            ownedBy: true,
          });
        }
      });
    this.isGoogleAutoCompleteWorking =
      this.prequalService.getIsGoogleAutoCompleteWorking();

    this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const formKeys = Object.keys(this.residenceForm.controls);
        for (const key of formKeys) {
          this.residenceForm.controls[
            key as keyof typeof residenceFormRules
          ].markAsTouched();
        }
        this.initFormChecks();
        this.cdRef.detectChanges();
        this.siblingValidationService.reportValidation(
          2,
          this.residenceForm.getRawValue(),
          this.residenceForm.valid
        );
      });
    this.initFormChecks();
    zip([
      this.awsService.prequalifiedJSONData$.pipe(
        tap((data) => {
          if (data) {
            this.globalText = data;
            this.formFieldData = data.residence;
          }

          this.usStates = this.formFieldData?.formsFields?.state?.options;
          this.ownedByList = this.formFieldData?.formsFields?.ownedBy?.options;
        })
      ),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }

  initFormChecks(): void {
    this.formIsIncomplete$ = this.residenceForm.statusChanges.pipe(
      map((status) => {
        const { addressLine2, ...requiredControls } =
          this.residenceForm.controls;
        return !(status === 'VALID' && !this.isWaiting);
      }),
      startWith(true)
    );
  }

  get f() {
    return this.residenceForm.controls;
  }

  clearNonAlphas(address: string) {
    return address.replace(/[^a-zA-Z0-9 ]/g, '');
  }

  public handleAddressChange(address: any) {
    let streetname = '';
    (address?.address_components || []).forEach(
      (address: {
        types: string | string[];
        short_name: string;
        long_name: any;
      }) => {
        if (address.types.includes('street_number')) {
          streetname = this.clearNonAlphas(address.short_name);
        }
        if (address.types.includes('route')) {
          this.residenceForm.controls.addressLine1.setValue(
            `${streetname ? streetname + ' ' : ''}${this.clearNonAlphas(
              address.long_name
            )}`
          );
          this.residenceForm.controls.addressLine1.markAsTouched();
        }
        if (address.types.includes('locality')) {
          this.residenceForm.controls.city.setValue(
            this.clearNonAlphas(address.long_name)
          );
          this.residenceForm.controls.city.markAsTouched();
        }
        if (address.types.includes('administrative_area_level_1')) {
          this.residenceForm.controls.userState.setValue(address.short_name);
          this.residenceForm.controls.userState.markAsTouched();
        }
        if (address.types.includes('postal_code')) {
          this.residenceForm.controls.zipCode.setValue(address.short_name);
          this.residenceForm.controls.zipCode.markAsTouched();
        }
      }
    );
  }
}
